// The project menu down the left side (Marius, side navigation option B, 4 Nov 2026; the design
// system's Sidebar part): the wordmark, the project, the main menu with the open section's tabs
// nested under it, and Settings and Help at the foot. Below 768px it is a drawer: the slim top
// bar's Menu button opens it, and Close, Escape, the backdrop or going to a page closes it.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router";
import { PAGES, pageLink } from "@convenor/shared";
import { NAV_ITEMS, navSection, ProjectSwitcher, type NavSection } from "./Header";
import { SectionTabList, useKnownSectionTabs } from "./SectionTabs";
import { Wordmark } from "./Wordmark";
import type { ProjectListItem } from "../api/project";

type Props = {
  /** The project the menu leads into: the address's, else the last one opened; null outside any. */
  homeProject: string | null;
  current: ProjectListItem | null;
  projects: ProjectListItem[];
  /** A draft (the onboarding): its name in the switcher, and no menu yet. */
  draftName: string | null;
  open: boolean;
  onClose: () => void;
};

export function Sidebar({ homeProject, current, projects, draftName, open, onClose }: Props) {
  const location = useLocation();
  const section = navSection(location.pathname);
  const known = useKnownSectionTabs();
  // The section whose tabs are unfolded. Moving to another section, the old one folds shut while
  // the new one folds open, together: at once when its tabs are known (it was open before), else
  // when its page hands them over, at most 0.6 s later (Edgar, 5 Nov 2026: "move with each other").
  const inSection = homeProject !== null ? section : null;
  const [unfolded, setUnfolded] = useState<NavSection>(inSection);
  useEffect(() => {
    if (unfolded === inSection) return;
    if (!inSection || inSection === "home" || known[inSection]) {
      setUnfolded(inSection);
      return;
    }
    const timer = window.setTimeout(() => setUnfolded(inSection), UNFOLD_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [inSection, known, unfolded]);
  const closeRef = useRef<HTMLButtonElement>(null);

  // The drawer: focus its Close button when it opens; Escape closes it.
  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const home = homeProject ? pageLink("projectHome", { projectId: homeProject }) : PAGES.projects;

  return (
    <>
      <aside id="sidebar" className={open ? "sidebar sidebar--open" : "sidebar"} aria-label="Project menu">
        <div className="sidebar__brand">
          <Wordmark to={home} />
          <button ref={closeRef} type="button" className="btn btn--quiet sidebar__close" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="sidebar__project">
          <ProjectSwitcher current={current} projects={projects} draftName={draftName} />
        </div>
        {draftName !== null ? null : (
          <nav className="sidebar__nav" aria-label="Main">
            {NAV_ITEMS.map((item) => {
              const isOpen = inSection === item.key;
              const tabs = known[item.key];
              const hasTabs = !!tabs?.tabs.length;
              const unfold = unfolded === item.key;
              return (
                <SidebarSection key={item.key} open={isOpen} hasTabs={isOpen && hasTabs} section={item.key} to={homeProject ? pageLink(item.page, { projectId: homeProject }) : PAGES.projects} label={item.label}>
                  {hasTabs ? (
                    <div className={unfold ? "sidebar__fold sidebar__fold--open" : "sidebar__fold"}>
                      <div>
                        <SectionTabList tabs={tabs} hidden={!unfold} />
                      </div>
                    </div>
                  ) : null}
                </SidebarSection>
              );
            })}
          </nav>
        )}
        <div className="sidebar__foot">
          <Link className="sidebar__item" to={PAGES.settingsTeam} aria-current={location.pathname.startsWith("/settings") ? "page" : undefined}>
            <Icon name="settings" />
            <span className="sidebar__label">Settings</span>
          </Link>
          <Link className="sidebar__item" to={PAGES.help} state={{ from: `${location.pathname}${location.search}` }} aria-current={location.pathname === PAGES.help ? "page" : undefined}>
            <Icon name="help" />
            <span className="sidebar__label">Help</span>
          </Link>
        </div>
      </aside>
      {/* The drawer's backdrop (a phone only): a tap closes the menu. Not a control for a keyboard, which has Close and Escape. */}
      <div className="sidebar__backdrop" onClick={onClose} aria-hidden="true" />
    </>
  );
}

/** How long a section waits for its page to hand over its tabs before it unfolds anyway. */
const UNFOLD_WAIT_MS = 600;

function SidebarSection({ section, to, label, open, hasTabs, children }: { section: Exclude<NavSection, null>; to: string; label: string; open: boolean; hasTabs: boolean; children: ReactNode }) {
  // The section you are in is bold and says so to a screen reader ("true": you are in it); without
  // tabs of its own (Home) it is the page you are on ("page"). It never changes while its tabs load.
  const className = open && hasTabs ? "sidebar__item sidebar__item--open" : "sidebar__item";
  return (
    <>
      <Link className={className} to={to} aria-current={open ? (hasTabs ? "true" : "page") : undefined}>
        <Icon name={section} />
        <span className="sidebar__label">{label}</span>
      </Link>
      {children}
    </>
  );
}

/** The menu's icons, drawn on Marius's board (16 by 16, shown at the part's icon size). */
function Icon({ name }: { name: Exclude<NavSection, null> | "settings" | "help" }) {
  return (
    <svg className="sidebar__icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
      {ICONS[name]}
    </svg>
  );
}

const ICONS: Record<Exclude<NavSection, null> | "settings" | "help", ReactNode> = {
  home: <path d="M2.5 7.5 8 3l5.5 4.5V13a.5.5 0 0 1-.5.5H9.5V10h-3v3.5H3a.5.5 0 0 1-.5-.5z" />,
  activities: (
    <>
      <rect x="2.5" y="3.5" width="11" height="10" rx="1.5" />
      <path d="M2.5 6.5h11M5.5 2v3M10.5 2v3" />
    </>
  ),
  experts: (
    <>
      <circle cx="8" cy="5.5" r="2.5" />
      <path d="M3 13.5c.8-2.6 2.7-3.7 5-3.7s4.2 1.1 5 3.7" />
    </>
  ),
  stakeholders: (
    <>
      <circle cx="6" cy="6" r="2.2" />
      <circle cx="11.5" cy="6.5" r="1.8" />
      <path d="M2 13c.6-2.2 2.1-3.2 4-3.2s3.4 1 4 3.2M10.5 10c1.6 0 2.9.9 3.5 2.8" />
    </>
  ),
  finance: (
    <>
      <rect x="2" y="3.5" width="12" height="9" rx="1.5" />
      <path d="M2 6.5h12M4.5 10h3" />
    </>
  ),
  reporting: <path d="M3 13V8M7 13V4M11 13V9M2 13.5h12" />,
  settings: (
    <>
      <circle cx="8" cy="8" r="2.2" />
      <path d="M8 1.8v1.6M8 12.6v1.6M1.8 8h1.6M12.6 8h1.6M3.6 3.6l1.1 1.1M11.3 11.3l1.1 1.1M3.6 12.4l1.1-1.1M11.3 4.7l1.1-1.1" />
    </>
  ),
  help: (
    <>
      <circle cx="8" cy="8" r="6" />
      <path d="M6.3 6.2a1.8 1.8 0 1 1 2.5 1.7c-.5.2-.8.6-.8 1.1v.4M8 11.6v.1" />
    </>
  ),
};
